import { useCallback, useMemo } from 'react';
import { css } from '@emotion/css';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import FilterActions from '@/components/Filter/FilterActions';
import UserFilterItem from '@/components/FilterManager/EntityFilter/entity/user/UserFilterItem';
import FilterVirtualList from '@/components/FilterNew/components/VirtualList';
import useFilterListActions from '@/components/FilterNew/hooks/useFilterActions';
import { useAppConfig } from '@/hooks/useAppConfig';
import { LanguageEnum } from '@/types/schema';

import { LanguageSelectorProps } from './types';

const FILTER_ITEM_HEIGHT = 40;
const FILTER_HEADER_HEIGHT_MOBILE = 56;
const MAX_FILTERBODY_HEIGHT = 400;
const LIST_VARIANT = 'multi-ungrouped-infinite' as any;

const filterBodyStyle = (height: number) => css`
  .filter-items {
    height: ${height >= MAX_FILTERBODY_HEIGHT ? MAX_FILTERBODY_HEIGHT : height}px;
  }
`;

const LanguageSelector = ({
  onChange,
  initialSelectedOptions,
  onToggle,
  className = 'w-[28rem] xs:w-full',
}: LanguageSelectorProps) => {
  const { isMobile } = useScreen();
  const {
    site: { supportedLanguages },
  } = useAppConfig();
  const itemRenderer = ({ option, isSelected }: any) => (
    <UserFilterItem isSelected={isSelected} option={option} />
  );

  const showActionButtons = true;

  const groups = useMemo(() => {
    const languageOptions = Object.entries(LanguageEnum)
      .filter(([key, value]) => {
        if (!supportedLanguages?.length) return true;
        return supportedLanguages.indexOf(value) != -1;
      })
      .map(([key, value]) => ({
        label: key,
        value: value,
      }))
      .filter(
        (languageOption) =>
          !initialSelectedOptions.find((option: any) => option.value == languageOption.value),
      );
    return [{ id: 'langauges', options: languageOptions, label: '' }];
  }, [LanguageEnum, supportedLanguages]);

  const totalOptions = useMemo(
    () => groups.reduce((acc, group) => acc + group.options.length, 0),
    [groups],
  );

  const { optionsActions } = useFilterListActions({
    initialSelectedOptions: [],
    keyExtractor: (val) => val.value,
    showActionButtons,
  });

  const onSave = useCallback(() => {
    if (!optionsActions.selectedOptions?.length) return;
    const seletedLanguages = optionsActions.selectedOptions.map(
      (selectedOption) => selectedOption.value as LanguageEnum,
    );
    onChange(seletedLanguages);
    onToggle?.(false);
  }, [onChange, onToggle, optionsActions.selectedOptions]);

  const onClear = useCallback(() => {
    onToggle?.(false);
  }, [onChange, onToggle]);

  const selectedSectionsCount = useMemo(() => {
    if (LIST_VARIANT === 'multi-ungrouped-finite') {
      return 1;
    }
    return 0;
  }, [initialSelectedOptions, LIST_VARIANT]);

  const memoizedBodyStyle = useMemo(() => {
    const groupLabelsCount = groups.map((group) => group?.label).filter(Boolean).length;
    const totalItemsCount = totalOptions + groupLabelsCount + selectedSectionsCount;
    const itemHeight = isMobile ? FILTER_HEADER_HEIGHT_MOBILE : FILTER_ITEM_HEIGHT;
    return filterBodyStyle(totalItemsCount * itemHeight + 12);
  }, [groups, isMobile, selectedSectionsCount, totalOptions]);

  return (
    <Box className={cn(className, memoizedBodyStyle)}>
      <Box className="h-full space-y-8 py-8">
        <FilterVirtualList
          fetchMore={undefined}
          fetching={false}
          groups={groups}
          initialSelectedOptions={[]}
          isSearching={false}
          itemRenderer={itemRenderer}
          listVariant={LIST_VARIANT}
          optionsActions={optionsActions}
        />
      </Box>
      <FilterActions onClear={onClear} onSave={onSave} />
    </Box>
  );
};

export default LanguageSelector;
